/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { useState, useCallback, useMemo } from 'react';

import { PageVignettesJsonType, VignettesJsonFileType } from '../algorithm/vignettes_types';
import { calculateNextPathwayStep, updateStateUserWatchedVideo } from './algorithm';
import { JourneyType, primaryJourneys, PrimaryJourneyType, secondaryJourneys, UserData } from './types';
interface VignettesPageProps {
  page: PageVignettesJsonType;
  json: VignettesJsonFileType;
}

export function VignettesPage({ page, json }: VignettesPageProps) {
  const [userData, setUserData] = useState<UserData>({
    flaggedJourneys: [],
    nonFlaggedJourneys: [],
    seenVignettes: new Set<string>(),
    currentState: {
      watchedVideosSinceLastQuiz: 0,
      lastJourneysWatched: [],
      lastJourneysWatchedDate: [],
    },
  });

  // Update specific userData field
  const updateUserData = useCallback((newUserData: Partial<UserData>) => {
    setUserData((prevUserData) => ({ ...prevUserData, ...newUserData }));
  }, []);

  /** This handles a tripple state button:
   *    - If in flaggedJourneys, remove from flaggedJourneys and add to nonFlaggedJourneys
   *    - If in nonFlaggedJourneys, remove from nonFlaggedJourneys
   *    - If in neither, add to flaggedJourneys
   */
  const handleTogglePrimaryJourney = (journey: PrimaryJourneyType) => {
    if (userData.flaggedJourneys.includes(journey)) {
      updateUserData({
        flaggedJourneys: userData.flaggedJourneys.filter((j) => j !== journey),
        nonFlaggedJourneys: [...userData.nonFlaggedJourneys, journey],
      });
    } else if (userData.nonFlaggedJourneys.includes(journey)) {
      updateUserData({ nonFlaggedJourneys: userData.nonFlaggedJourneys.filter((j) => j !== journey) });
    } else {
      updateUserData({ flaggedJourneys: [...userData.flaggedJourneys, journey] });
    }
  };
  /** For second journeys, it's only flagged or not:
   *    - If in flaggedJourneys, remove from flaggedJourneys
   *    - otherwise, add to flaggedJourneys
   */
  const handleToggleSecondaryJourney = (journey: JourneyType) => {
    if (userData.flaggedJourneys.includes(journey)) {
      updateUserData({ flaggedJourneys: userData.flaggedJourneys.filter((j) => j !== journey) });
    } else {
      updateUserData({ flaggedJourneys: [...userData.flaggedJourneys, journey] });
    }
  };

  const nextPathwayStep = useMemo(() => calculateNextPathwayStep(json, userData, new Date(), false), [userData, json]);

  const handleWatchVideoClick = useCallback(
    (vignetteID: string, didQuiz: boolean = true) => {
      setUserData((prevUserData) => updateStateUserWatchedVideo(json, prevUserData, vignetteID, didQuiz, new Date()));
    },
    [json]
  );
  if (!nextPathwayStep) {
    return <div>All videos watched!</div>;
  }

  const { nextVignetteID, nextVignette, nextNextVignette } = nextPathwayStep;
  return (
    <div>
      <div>{Object.keys(json.vignettes).length} vignettes</div>
      <div>{Object.keys(json.journeys).length} journeys</div>
      <br />
      {/** For each journey, make a toggle button to mark whether we are following that journey or not */}
      <div>
        <div>
          {primaryJourneys.map((journey) => (
            <button
              key={journey}
              onClick={() => handleTogglePrimaryJourney(journey)}
              style={{
                backgroundColor: userData.flaggedJourneys.includes(journey)
                  ? '#F28C8C'
                  : userData.nonFlaggedJourneys.includes(journey)
                  ? 'var(--lightgreen)'
                  : 'var(--white)',
                cursor: 'pointer',
              }}
            >
              {journey}
            </button>
          ))}
        </div>
        <div>
          {secondaryJourneys.map((journey) => (
            <button
              key={journey}
              onClick={() => handleToggleSecondaryJourney(journey)}
              style={{
                backgroundColor: userData.flaggedJourneys.includes(journey) ? '#F28C8C' : 'var(--white)',
                cursor: 'pointer',
              }}
            >
              {journey}
            </button>
          ))}
        </div>
      </div>
      <br />
      <div>
        <h1>{nextVignette?.name}</h1>
        <button onClick={() => handleWatchVideoClick(nextVignetteID ?? '')}>Watched video</button>
        <div
          style={{
            whiteSpace: 'pre-wrap',
            margin: 50,
          }}
        >
          <i>{nextVignette?.transcript}</i>
        </div>
        <ul>
          {nextVignette?.bulletPoints.map((bp) => (
            <li key={bp.text}>{bp.text}</li>
          ))}
        </ul>
        <ol>
          {nextVignette?.quiz.questions.map((question) => (
            <li key={question.q_id}>
              <div>{question.question}</div>
              {question.answers.map((answer) => (
                <div key={answer.id}>
                  <input
                    type="radio"
                    name={question.q_id}
                    id={answer.id}
                    checked={answer.correct}
                    readOnly
                    style={{ paddingRight: 5 }}
                  />
                  <label htmlFor={answer.id}>{answer.text}</label>
                  <br />
                  <i style={{ color: 'lightgray', paddingLeft: 20 }}>{answer.explanation}</i>
                </div>
              ))}
            </li>
          ))}
        </ol>
        <br />
        <div>Up next: {nextNextVignette?.name}</div>
        <button onClick={() => handleWatchVideoClick(nextVignetteID ?? '')}>Watched video</button>
      </div>
    </div>
  );
}
